/**
 * 3-bucket chip variant component — Multi-Add Phase 2 (PRD §2.4 + §2.11).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The variant
 * scheme:
 *
 *   - `fully-configured`     — no chip (component returns null)
 *   - `needs-attention`      — red, alert-circle icon  (auth INVALID)
 *   - `added-with-warnings`  — yellow/warning, alert-triangle icon
 *     (video VALIDATED but control INVALID/UNREACHABLE — partial success)
 *   - `failed`               — red, close-circle icon (no state-sync entry;
 *     rendered by the failed-tile placeholder over the `failedDevices`
 *     slice)
 *
 * Per `ui-architecture` (ai.knowledge/global): all colors resolve via
 * Tailwind theme tokens (`error`, `warning`) so the Hive whitelabel
 * variant inherits the theme. NO literal Tailwind colors / hex / rgb.
 *
 * Per Design lead a11y point: icon + color + copy must be three
 * independent discriminators — colorblind users must distinguish
 * variants via icon shape, not color alone. The three icons below have
 * structurally-distinct SVG paths (circle / triangle / circled-X).
 *
 * Selector pattern: when this chip is mounted on a tile that subscribes
 * to depot state-sync (`<Item>` in `video/sources/item/`), the
 * SUBSCRIPTION lives in the parent and `variant` arrives via prop. The
 * chip itself is a pure render of (variant, label, onClick) — no depot
 * reads inside. This keeps the chip unit-testable in isolation (no
 * proxy / locateDevice / state-sync setup needed).
 *
 * onClick is a pass-through callback. The deep-link routing logic
 * lives at the CALL SITE, not in this component. Per `stay-in-scope`
 * (ai.knowledge/global) — this codebase already has a deep-link
 * channel for Advanced Settings:
 * `useSettings(device, { defaultPanel, defaultTab })` opens the
 * settings DIALOG with a pre-selected sub-tab (see
 * `room/components/video/hooks.ts::useSettings` and the
 * `chipClickOptions` pattern at
 * `room/components/video/sources/item/chip-click.ts`). Callers wire
 * their own onClick using that channel; the chip stays a pure-render
 * primitive so the same component can be re-used in surfaces where
 * the click should NOT navigate (e.g. the inline chip on a discovery-
 * table row that's already in the bulk-add modal).
 *
 * The pre-monorepo design referenced `useNavigate(advancedSettingsUrl,
 * { state: { defaultSection } })` — that route-based pattern does NOT
 * exist in this monorepo (Settings is a Dialog, not a routed page).
 * The current implementation adopts the existing dialog deep-link
 * channel; see
 * `video/components/settings/iris-connection/skip-reason-to-tab.ts`
 * for the `MultiAddSkipReason -> IrisConnectionTab` mapping used by
 * the callers.
 */
import type { MouseEvent, ReactElement } from 'react';
import { Icon } from '@studio/design-system/icon';
import { twMerge } from 'cn';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { emitMultiAddAnalytics } from '#~/analytics/utils/emit-multi-add-analytics.js';
import type { ChipVariant } from './chip-variant.js';

export interface ChipProps {
  /** 3-bucket discriminated variant. `'fully-configured'` renders null. */
  variant: ChipVariant;

  /**
   * User-facing chip copy. The variant maps to a default copy via the
   * `defaultChipLabel` helper in `chip-variant.ts`, but the call site
   * can override (e.g. failed-tile renders the discriminated reason
   * "Authentication failed" / "Linked to another studio" / etc.).
   *
   * Per `exactOptionalPropertyTypes` (tsconfig) — explicit `| undefined`
   * lets callers pass a `string | undefined` expression directly without
   * a spread-conditional wrapper.
   */
  label?: string | undefined;

  /** Optional click handler — typically opens device settings. */
  onClick?: (() => void) | undefined;

  /** Optional CSS overrides for positioning at the call site. */
  className?: string | undefined;
}

/**
 * Map a variant to its icon class. Icon names match the iconify
 * convention used elsewhere in the codebase (see existing
 * `<Icon icon='alert-triangle'>` / `<Icon icon='alert-circle'>` /
 * `<Icon icon='close'>` call sites in summary.tsx, fallback.tsx).
 *
 * a11y: distinct icons per variant. AlertCircle, AlertTriangle, and
 * close-circle have structurally-distinct outlines — a colorblind user
 * can tell them apart from shape alone.
 */
const iconForVariant = (variant: ChipVariant): string | null => {
  switch (variant) {
    case 'needs-attention':
      return 'alert-circle';
    case 'added-with-warnings':
      return 'alert-triangle';
    case 'failed':
      return 'close-circle';
    case 'fully-configured':
    default:
      return null;
  }
};

/**
 * Map a variant to its color token class. All values resolve to the
 * design-system theme tokens so the Hive whitelabel applies its own
 * brand colors without forking the component. Per
 * `ui-architecture` — never literal Tailwind color classes.
 */
const colorClassForVariant = (variant: ChipVariant): string => {
  switch (variant) {
    case 'needs-attention':
      // Red — auth INVALID on either aspect (cannot connect at all).
      return 'text-error';
    case 'added-with-warnings':
      // Yellow — partial success (video OK, control failing/missing).
      return 'text-warning';
    case 'failed':
      // Red, but the failed-tile placeholder typically wraps the chip
      // in a gray background so the chip surface itself reads as part
      // of the same "broken" visual band.
      return 'text-error';
    case 'fully-configured':
    default:
      return '';
  }
};

/**
 * 3-bucket chip render. Returns `null` for the `fully-configured`
 * variant (no chip rendered for healthy devices).
 *
 * Per Design peer review + a11y audit: when an onClick is supplied the
 * chip renders as a native `<button>`. Native button semantics give us
 * Enter + Space activation, focus ring, and screen-reader announcement
 * for free — no `role='button'` + `tabIndex` + keyDown handler pattern
 * needed. Click stopPropagation prevents the parent tile / row's
 * selection toggle from firing alongside.
 *
 * When no onClick is supplied (decorative chip — e.g. inline chip on a
 * discovery-table row where the row's selection toggle is the relevant
 * interaction) the chip renders as a non-interactive `<span>` (inline
 * element, no button semantics).
 */
export const Chip = ({ variant, label, onClick, className }: ChipProps): ReactElement | null => {
  // `fully-configured` is the no-chip path — Design + Eng locked: no
  // visual residue for healthy devices.
  if (variant === 'fully-configured') {
    return null;
  }

  const iconName = iconForVariant(variant);
  const colorClass = colorClassForVariant(variant);
  const resolvedLabel = label ?? defaultLabelForVariant(variant);

  // Stop propagation so a chip click inside a tile / row doesn't bubble
  // into the parent's selection-toggle handler. Native `<button>` fires
  // `click` on Enter + Space, so this single handler covers mouse +
  // keyboard activation paths. Per existing chip contract (see
  // `needs-attention-chip.tsx` for the prior-art).
  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    // Track the chip click (Multi-Add Phase 2 §2.3/§2.4) — e.g. the
    // "Authenticate" chip that deep-links a needs-auth placeholder into
    // Advanced Settings. The chip stays a dumb pass-through for routing
    // (callers own the onClick), but the click itself is a funnel event;
    // emitting here is the single place that captures EVERY chip click
    // regardless of which surface mounted it. `variant` is never
    // 'fully-configured' here (that path returned null above), so the
    // cast is exhaustive over the interactive variants.
    // Per `reference-rules-in-comments` (ai.knowledge/global).
    emitMultiAddAnalytics(EVENT_NAMES.MULTI_ADD_CHIP_CLICKED, {
      chipVariant: variant as 'needs-attention' | 'added-with-warnings' | 'failed'
    });
    onClick?.();
  };

  // onClick is intentionally a pass-through (Multi-Add Phase 2 §2.3).
  // Callers (the failed-tile placeholder, the notification panel row,
  // and the per-tile in-room chip) build the deep-link options
  // themselves via `skipReasonToDefaultTab` +
  // `useSettings(device, options)`. Keeping the chip dumb means it can
  // also render in surfaces where the click should NOT navigate (e.g.
  // the inline chip on a discovery-table row inside the bulk-add
  // modal, where the row's selection toggle is the relevant
  // interaction).

  const sharedClassName = twMerge(
    // Layout: pill shape with icon + text.
    'inline-flex items-center gap-4 rounded px-8 py-4 text-sm',
    // Dark background so the colored icon + text read on light + dark.
    'bg-base-100/90',
    // Keyboard ring uses the variant color for affordance parity.
    onClick && 'cursor-pointer outline-none focus-visible:ring-2',
    onClick && variant === 'needs-attention' && 'focus-visible:ring-error',
    onClick && variant === 'added-with-warnings' && 'focus-visible:ring-warning',
    onClick && variant === 'failed' && 'focus-visible:ring-error',
    colorClass,
    className
  );

  const inner = (
    <>
      {iconName !== null && (
        <Icon
          // `aria-hidden` because the icon is supplementary — the
          // `aria-label` on the chip already carries the verbal content.
          aria-hidden='true'
          icon={iconName}
          className='text-[14px]'
        />
      )}
      <span className='font-medium'>{resolvedLabel}</span>
    </>
  );

  // Per audit C4 (2026-05-13): native `<button>` instead of
  // `<div role="button" tabIndex={0}>`. Native semantics for free.
  if (onClick) {
    return (
      <button
        type='button'
        aria-label={resolvedLabel}
        onClick={handleClick}
        className={sharedClassName}
      >
        {inner}
      </button>
    );
  }

  return (
    <span aria-label={resolvedLabel} className={sharedClassName}>
      {inner}
    </span>
  );
};

/**
 * Default copy per variant. Locked strings per plan §W3 (Design lead
 * recommendation pending Product approval — TODO(Product) marker).
 *
 * TODO(Product): final copy lock. Plan calls out three candidate strings:
 *   - "Needs attention" / "Added with warnings" / "Failed"
 *   These ship as reasonable defaults; Product may revise.
 */
const defaultLabelForVariant = (variant: ChipVariant): string => {
  switch (variant) {
    case 'needs-attention':
      return 'Needs attention';
    case 'added-with-warnings':
      return 'Added with warnings';
    case 'failed':
      return 'Failed';
    case 'fully-configured':
    default:
      return '';
  }
};
